import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Activity, Clock, CheckCircle2, AlertTriangle, CalendarCheck } from 'lucide-react';
import { reportsApi, type KpiMetric } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { Dialog } from '@/components/ui/dialog';
import { Badge, statusColor, priorityColor } from '@/components/ui/badge';

interface Props {
  startDate: string; // ISO
  endDate:   string; // ISO
}

function fmtHours(h: number | null): string {
  // Treat null and exact zero the same way — both mean "no meaningful data".
  // A literal 0-hour gap means overlapping timestamps, not a useful KPI value.
  if (h === null || h === undefined || h === 0) return '—';
  if (h < 1)    return `${(h * 60).toFixed(0)}m`;
  if (h < 24)   return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function KpiCard({
  label, value, sub, icon: Icon, color, valueTone, onClick,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: typeof Activity;
  color: 'indigo' | 'amber' | 'teal' | 'sky' | 'violet' | 'red';
  valueTone?: 'good' | 'warn' | 'bad';
  onClick?: () => void;
}) {
  const colors = {
    indigo: 'bg-brand-700 text-white',
    amber:  'bg-amber-700 text-white',
    teal:   'bg-teal-700 text-white',
    sky:    'bg-sky-700 text-white',
    violet: 'bg-violet-700 text-white',
    red:    'bg-red-700 text-white',
  };
  const borders = {
    indigo: 'border-l-brand-600',
    amber:  'border-l-amber-600',
    teal:   'border-l-teal-600',
    sky:    'border-l-sky-600',
    violet: 'border-l-violet-600',
    red:    'border-l-red-600',
  };
  const valueClass = valueTone
    ? { good: 'text-green-700', warn: 'text-amber-700', bad: 'text-red-700' }[valueTone]
    : 'text-ink-900';
  const inner = (
    <div className="flex items-start gap-3">
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-ink-400 font-medium uppercase tracking-wide">{label}</p>
        <p className={`text-2xl font-semibold font-mono tracking-tight mt-0.5 ${valueClass}`}>{value}</p>
        {sub && <p className="text-xs text-ink-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
  const base = `bg-white rounded-lg border border-canvas-line border-l-4 ${borders[color]} px-5 py-4 text-left transition-colors`;
  if (!onClick) return <div className={base}>{inner}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} hover:bg-canvas-surface focus:outline-none focus:ring-2 focus:ring-brand-500`}
    >
      {inner}
    </button>
  );
}

const METRIC_TITLES: Record<KpiMetric, string> = {
  mtbf:          'MTBF — Failures by equipment',
  mttr:          'MTTR — Longest repairs',
  pm_compliance: 'PM Compliance — closed PM work orders',
  total_wo:      'Total Work Orders in period',
};

function KpiDetailsModal({
  metric, open, onClose, startDate, endDate,
}: {
  metric: KpiMetric | null;
  open:   boolean;
  onClose: () => void;
  startDate: string;
  endDate:   string;
}) {
  const navigate = useNavigate();
  const [showLateOnly, setShowLateOnly] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'kpi-details', metric, startDate, endDate],
    queryFn:  () => reportsApi.kpiDetails(metric!, { start_date: startDate, end_date: endDate }),
    enabled:  open && metric !== null,
  });

  if (!metric) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={METRIC_TITLES[metric]}
      className="max-w-4xl"
    >
      {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
      {error    && <p className="text-sm text-red-600">Failed to load details.</p>}

      {data && data.rows.length === 0 && (
        <p className="py-6 text-center text-slate-400 text-sm">No data for this period.</p>
      )}

      {/* MTBF */}
      {data?.metric === 'mtbf' && data.rows.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            Equipment with 2+ corrective work orders in the period. Sorted by shortest recent gap (worst first).
          </p>
          <Table>
            <Thead>
              <tr>
                <Th>Asset Tag</Th>
                <Th>Equipment</Th>
                <Th className="text-right">Failures</Th>
                <Th className="text-right">Avg Gap</Th>
                <Th className="text-right">Last Gap</Th>
              </tr>
            </Thead>
            <Tbody>
              {data.rows.map(r => (
                <Tr key={r.equipment_id} onClick={() => { onClose(); navigate(`/equipment/${r.equipment_id}`); }}>
                  <Td className="font-mono text-sm">{r.asset_tag}</Td>
                  <Td className="text-sm font-medium text-brand-700">{r.name}</Td>
                  <Td className="text-right text-sm">{r.failure_count}</Td>
                  <Td className="text-right text-sm">{fmtHours(r.avg_gap_hours)}</Td>
                  <Td className="text-right text-sm font-semibold">{fmtHours(r.last_gap_hours)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
      )}

      {/* MTTR */}
      {data?.metric === 'mttr' && data.rows.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            Closed work orders in the period, sorted by longest repair duration first.
          </p>
          <Table>
            <Thead>
              <tr>
                <Th>WO #</Th>
                <Th>Title</Th>
                <Th>Equipment</Th>
                <Th className="text-right">Duration</Th>
              </tr>
            </Thead>
            <Tbody>
              {data.rows.map(r => (
                <Tr key={r.wo_id} onClick={() => { onClose(); navigate(`/work-orders/${r.wo_id}`); }}>
                  <Td className="font-mono text-xs text-slate-500 whitespace-nowrap">{r.wo_number}</Td>
                  <Td className="text-sm font-medium max-w-[16rem] truncate">{r.title}</Td>
                  <Td className="text-sm text-slate-600">{r.equipment_name}</Td>
                  <Td className="text-right text-sm font-semibold">{fmtHours(r.duration_hours)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
      )}

      {/* PM Compliance */}
      {data?.metric === 'pm_compliance' && data.rows.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500">
              Closed PM work orders in the period. {data.rows.filter(r => !r.was_on_time).length} late of {data.rows.length}.
            </p>
            <label className="text-xs text-slate-600 flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showLateOnly}
                onChange={e => setShowLateOnly(e.target.checked)}
                className="rounded"
              />
              Late only
            </label>
          </div>
          <Table>
            <Thead>
              <tr>
                <Th>WO #</Th>
                <Th>Title</Th>
                <Th>Equipment</Th>
                <Th className="text-right">Status</Th>
              </tr>
            </Thead>
            <Tbody>
              {data.rows.filter(r => !showLateOnly || !r.was_on_time).map(r => (
                <Tr key={r.wo_id} onClick={() => { onClose(); navigate(`/work-orders/${r.wo_id}`); }}>
                  <Td className="font-mono text-xs text-slate-500 whitespace-nowrap">{r.wo_number}</Td>
                  <Td className="text-sm font-medium max-w-[16rem] truncate">{r.title}</Td>
                  <Td className="text-sm text-slate-600">{r.equipment_name}</Td>
                  <Td className="text-right text-sm">
                    {r.was_on_time
                      ? <span className="text-emerald-600 font-medium">On time</span>
                      : <span className="text-red-600 font-medium">{r.days_late}d late</span>}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
      )}

      {/* Total WOs */}
      {data?.metric === 'total_wo' && data.rows.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            All {data.rows.length} work orders created in the period, newest first.
          </p>
          <Table>
            <Thead>
              <tr>
                <Th>WO #</Th>
                <Th>Title</Th>
                <Th>Equipment</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
              </tr>
            </Thead>
            <Tbody>
              {data.rows.map(r => (
                <Tr key={r.wo_id} onClick={() => { onClose(); navigate(`/work-orders/${r.wo_id}`); }}>
                  <Td className="font-mono text-xs text-slate-500 whitespace-nowrap">{r.wo_number}</Td>
                  <Td className="text-sm font-medium max-w-[16rem] truncate">{r.title}</Td>
                  <Td className="text-sm text-slate-600">{r.equipment_name}</Td>
                  <Td><Badge color={statusColor(r.status)}>{r.status.replace(/_/g, ' ')}</Badge></Td>
                  <Td><Badge color={priorityColor(r.priority)}>{r.priority}</Badge></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
      )}
    </Dialog>
  );
}

/**
 * Maintenance KPIs — MTBF, MTTR, PM compliance, Top offenders.
 *
 * Pro-tier feature. Rendered conditionally by the Reports page based on
 * `useAuth().hasPro`. The backend endpoint also enforces requireTier('pro').
 */
export function KpiPanel({ startDate, endDate }: Props) {
  const { hasPro } = useAuth();
  const navigate = useNavigate();
  const [activeMetric, setActiveMetric] = useState<KpiMetric | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'kpis', startDate, endDate],
    queryFn:  () => reportsApi.kpis({ start_date: startDate, end_date: endDate }),
    enabled:  hasPro,
  });

  if (!hasPro) return null;

  if (isLoading) {
    return <p className="text-sm text-slate-400">Loading KPIs…</p>;
  }
  if (error || !data) {
    return <p className="text-sm text-red-600">Failed to load KPIs.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="MTBF"
          value={fmtHours(data.mtbf_hours)}
          sub="Mean time between failures"
          icon={Activity}
          color="indigo"
          onClick={() => setActiveMetric('mtbf')}
        />
        <KpiCard
          label="MTTR"
          value={fmtHours(data.mttr_hours)}
          sub="Mean time to repair"
          icon={Clock}
          color="amber"
          onClick={() => setActiveMetric('mttr')}
        />
        <KpiCard
          label="PM Compliance"
          value={data.pm_compliance_pct === null ? '—' : `${data.pm_compliance_pct}%`}
          sub="On-time scheduled PMs"
          icon={CheckCircle2}
          color="teal"
          valueTone={data.pm_compliance_pct === null
            ? undefined
            : data.pm_compliance_pct >= 80 ? 'good' : data.pm_compliance_pct >= 50 ? 'warn' : 'bad'}
          onClick={() => setActiveMetric('pm_compliance')}
        />
        <KpiCard
          label="Total WOs"
          value={String(data.total_work_orders)}
          sub="In the selected period"
          icon={AlertTriangle}
          color="sky"
          onClick={() => setActiveMetric('total_wo')}
        />
        <KpiCard
          label="Planned Work"
          value={data.planned_pct === null ? '—' : `${data.planned_pct}%`}
          sub={`${data.planned_count} planned · ${data.reactive_count} reactive`}
          icon={CalendarCheck}
          color="violet"
          valueTone={data.planned_pct === null
            ? undefined
            : data.planned_pct >= 80 ? 'good' : data.planned_pct >= 50 ? 'warn' : 'bad'}
        />
      </div>

      <Card>
        <CardHeader title="Top Offenders" />
        <CardBody className="p-0">
          {data.top_offenders.length === 0 ? (
            <p className="px-5 py-6 text-center text-slate-400 text-sm">No work orders in this period.</p>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Rank</Th>
                  <Th>Asset Tag</Th>
                  <Th>Equipment</Th>
                  <Th>WO Count</Th>
                </tr>
              </Thead>
              <Tbody>
                {data.top_offenders.map((eq, i) => (
                  <Tr
                    key={eq.equipment_id}
                    onClick={() => navigate(`/equipment/${eq.equipment_id}`)}
                  >
                    <Td className="font-mono text-sm text-slate-500">#{i + 1}</Td>
                    <Td className="font-mono text-sm">{eq.asset_tag}</Td>
                    <Td className="text-sm font-medium text-brand-700">{eq.name}</Td>
                    <Td className="text-sm font-semibold text-slate-900">{eq.count}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <KpiDetailsModal
        metric={activeMetric}
        open={activeMetric !== null}
        onClose={() => setActiveMetric(null)}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
}
